import { apiCall } from ".";
import { getSession } from "../handlers/getSession";
import { getTenant } from "../handlers/getTenant";

/**
 * Search for external employees in MercadoLibre
 * @param {Object} params - Search parameters
 * @param {string} params.firstName - First name (Nombre)
 * @param {string} params.lastName - Last name (Apellido)
 * @param {string} [params.country] - Country code (Pais)
 * @param {string} [params.email] - Email
 * @param {string} [params.businessName] - Business name (RazonSocial)
 * @returns {Promise<Object>} Search results
 */
export const searchExternalEmployees = async ({
  firstName,
  lastName,
  country,
  email,
  businessName,
}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return;

  const params = new URLSearchParams();
  params.append("employeeType", "external");

  if (firstName) params.append("Nombre", firstName);
  if (lastName) params.append("Apellido", lastName);
  if (country) params.append("Pais", country);
  if (email) params.append("Email", email);
  if (businessName) params.append("RazonSocial", businessName);

  try {
    const resp = await apiCall.get(
      `${tenant}/api/mercadolibre/employees/search/?${params.toString()}`,
      {
        headers: {
          IdToken: session.IdToken,
          Authorization: `Bearer ${session.AccessToken}`,
        },
      }
    );
    return resp.data;
  } catch (error) {
    console.error("Error searching external employees:", error);
    throw error;
  }
};

/**
 * Search for internal employees in MercadoLibre
 * @param {Object} params - Search parameters
 * @param {string} params.firstName - First name
 * @param {string} params.lastName - Last name
 * @param {string} [params.area] - Area code
 * @param {string} [params.puesto] - Position code (puesto)
 * @param {string} [params.countryOfCompany] - Country code (3 chars: BRA, ARG, MEX)
 * @param {string} [params.location] - Location code
 * @returns {Promise<Object>} Search results
 */
/**
 * Get employee details from MercadoLibre
 * @param {Object} params - Parameters
 * @param {string} params.userId - User ID
 * @param {string} params.employeeType - Employee type (internal/external)
 * @returns {Promise<Object>} Employee details
 */
export const getEmployeeDetail = async ({ userId, employeeType }) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return;

  const params = new URLSearchParams();
  params.append("userId", userId);
  params.append("employeeType", employeeType);

  try {
    const resp = await apiCall.get(
      `${tenant}/api/mercadolibre/employees/detail/?${params.toString()}`,
      {
        headers: {
          IdToken: session.IdToken,
          Authorization: `Bearer ${session.AccessToken}`,
        },
      }
    );
    return resp.data;
  } catch (error) {
    console.error("Error getting employee detail:", error);
    throw error;
  }
};

export const searchInternalEmployees = async ({
  firstName,
  lastName,
  department,
  puesto,
  countryOfCompany,
  location,
}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return;

  const params = new URLSearchParams();
  params.append("employeeType", "internal");

  if (firstName) params.append("firstName", firstName);
  if (lastName) params.append("lastName", lastName);
  // Bare FODepartment code (71000564), without the catalogue's "X" prefix
  if (department) params.append("department", department);
  if (puesto) params.append("puesto", puesto);
  if (countryOfCompany) params.append("countryOfCompany", countryOfCompany);
  if (location) params.append("location", location);

  try {
    const resp = await apiCall.get(
      `${tenant}/api/mercadolibre/employees/search/?${params.toString()}`,
      {
        headers: {
          IdToken: session.IdToken,
          Authorization: `Bearer ${session.AccessToken}`,
        },
      }
    );
    return resp.data;
  } catch (error) {
    console.error("Error searching internal employees:", error);
    throw error;
  }
};

/**
 * Get employee career / performance info (linea-de-denuncias info_usuario endpoint).
 *
 * Se consulta en TIEMPO REAL cada vez que se despliega el toggle "Detalle de
 * Carrera". No se persiste. La respuesta es un wrapper { status, message, data }
 * donde `data` contiene el objeto de carrera (full_name, on_pip, pip_detail,
 * exit_history, data, ...).
 *
 * @param {Object} params - Parameters
 * @param {string} params.username - Employee username (e.g., 'llggoes')
 * @param {string|number} params.legajo - Employee file/payroll number (Nº empleado)
 * @returns {Promise<Object>} Career info object (the unwrapped `data` payload)
 */
export const getInfoUsuario = async ({ username, legajo }) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return;

  const params = new URLSearchParams();
  params.append("username", username);
  //params.append("legajo", legajo);
  params.append("legajo", "20002");

  try {
    const resp = await apiCall.get(
      `${tenant}/api/mercadolibre/info-usuario/?${params.toString()}`,
      {
        headers: {
          IdToken: session.IdToken,
          Authorization: `Bearer ${session.AccessToken}`,
        },
      }
    );
    return resp.data?.data ?? null;
  } catch (error) {
    // El backend siempre responde 502 en fallo, con el detalle en `message`.
    // Distinguimos "colaborador no encontrado" (404) de un error genérico.
    const message = error?.response?.data?.message || "";
    const notFound =
      error?.response?.status === 404 ||
      /status 404/i.test(message) ||
      /no user found/i.test(message);

    const err = new Error(message || "Error getting employee career info");
    err.code = notFound ? "not_found" : "api_error";
    console.error("Error getting employee career info:", error);
    throw err;
  }
};

/**
 * Assign report to PBP Leader
 * @param {Object} params - Parameters
 * @param {string|number} params.report_id - Report ID
 * @param {string} params.email - PBP Leader email
 * @param {string} params.first_name - PBP Leader first name
 * @param {string} params.last_name - PBP Leader last name
 * @returns {Promise<Object>} Assignment result
 */
export const assignReportToPbpLeader = async ({
  report_id,
  email,
  first_name,
  last_name,
}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return;

  try {
    const resp = await apiCall.post(
      `${tenant}/api/mercadolibre/pbp-assignment/`,
      {
        report_id,
        email,
        first_name,
        last_name,
      },
      {
        headers: {
          IdToken: session.IdToken,
          Authorization: `Bearer ${session.AccessToken}`,
        },
      }
    );
    return resp.data;
  } catch (error) {
    console.error("Error assigning report to PBP Leader:", error);
    throw error;
  }
};
